import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import moment from 'moment';

import LightTooltip from '../../UI/LightTooltip/LightTooltip';
import OverflowTooltip from '../../UI/OverflowTooltip/OverflowTooltip';
import { EstadoComprobanteChip } from '../EstadoComprobanteTag/EstadoComprobanteTag';
import { formatNumberWithMonedaSigno } from '../../../assets/shared/formatNumber';
import { nombreComprobante } from '../textosComprobante';
import { esImputacion, resumenEnlace, detalleArticulos } from './armarCircuito';
import ValoresRecibo from './ValoresRecibo';
import colores from '../Bandeja/coloresBandeja';

const useStyles = makeStyles(() => ({
  nodo: {
    border: `1px solid ${colores.borde}`,
    borderRadius: '6px',
    backgroundColor: 'white',
    padding: '8px 12px',
    fontSize: '13px',
    minWidth: 0
  },
  raiz: {
    borderColor: colores.naranja,
    borderLeftWidth: '4px'
  },
  linea: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0 8px'
  },
  nombre: {
    fontWeight: 500
  },
  dato: {
    color: colores.tenue,
    fontSize: '12px',
    whiteSpace: 'nowrap'
  },
  cobrado: {
    color: colores.ok,
    fontSize: '12px'
  },
  anulado: {
    textDecoration: 'line-through',
    color: colores.tenue
  },
  articulos: {
    color: colores.primarioOscuro,
    fontSize: '12px',
    cursor: 'help'
  },
  recibo: {
    borderLeft: `4px solid ${colores.ok}`
  },
  imputado: {
    color: colores.ok,
    fontSize: '12px',
    fontWeight: 500
  },
  desplegar: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '2px',
    marginTop: '4px',
    padding: 0,
    border: 0,
    background: 'none',
    cursor: 'pointer',
    color: colores.primarioOscuro,
    fontSize: '12px',
    fontFamily: 'inherit'
  }
}));

/**
 * Un comprobante del circuito: tipo y número, fecha, total, si está anulado y, si se pasa enlace, lo que descargó
 * del comprobante anterior (o, si es un recibo, lo que le imputó). Las facturas muestran lo cobrado y los recibos
 * despliegan sus valores.
 */
const NodoCircuito = ({ nodo, enlace, esRaiz, circuito }) => {
  const classes = useStyles();
  const [valoresAbiertos, setValoresAbiertos] = useState(false);
  const parcial = nodo.remunerativo && nodo.proporcion < 0.9999;

  return (
    <div className={`${classes.nodo} ${esRaiz ? classes.raiz : ''} ${nodo.esRecibo ? classes.recibo : ''}`}>
      <div className={classes.linea}>
        <OverflowTooltip className={`${classes.nombre} ${nodo.anulado ? classes.anulado : ''}`}>{nombreComprobante(nodo)}</OverflowTooltip>
        {nodo.anulado && <EstadoComprobanteChip estado="Anulado" style={{ height: '18px' }} />}
      </div>
      <div className={classes.linea}>
        <span className={classes.dato}>{nodo.fecha ? moment(nodo.fecha).format('DD/MM/YYYY') : ''}</span>
        <span className={classes.dato}>{nodo.esRecibo ? `Total del recibo ${formatNumberWithMonedaSigno(nodo.total)}` : formatNumberWithMonedaSigno(nodo.total)}</span>
      </div>
      {enlace &&
        (esImputacion(enlace) ? (
          <div className={classes.imputado}>{resumenEnlace(enlace)}</div>
        ) : (
          <LightTooltip title={<span style={{ whiteSpace: 'pre-line' }}>{detalleArticulos(enlace)}</span>} placement="top-start">
            <div className={classes.articulos}>{resumenEnlace(enlace)}</div>
          </LightTooltip>
        ))}
      {nodo.remunerativo && !nodo.anulado && !nodo.esRecibo && (
        <div className={classes.cobrado}>
          Cobrado {formatNumberWithMonedaSigno(nodo.pagado)}
          {parcial && ` · ${Math.round(nodo.proporcion * 100)}% corresponde a este pedido`}
        </div>
      )}
      {nodo.esRecibo && (
        <React.Fragment>
          <button type="button" className={classes.desplegar} onClick={() => setValoresAbiertos(!valoresAbiertos)}>
            {valoresAbiertos ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            {valoresAbiertos ? 'Ocultar valores' : 'Ver valores'}
          </button>
          {valoresAbiertos && <ValoresRecibo recibo={nodo} circuito={circuito} />}
        </React.Fragment>
      )}
    </div>
  );
};

export default NodoCircuito;
